import { ScrollableList } from '@ps/isp-web-component-library';
import type { LinkProps } from '@ps/web-shared-components';
import { generateSportsPath } from '@ps/web-sports-utils';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';
import { ariaLabelScrollLeft, ariaLabelScrollRight } from '@swf/sports/utils/getAriaLabels';

import { SportsLink } from '../SportsLink/SportsLink';

const getActiveProp = (
  isActive: (() => boolean) | undefined,
  pathname: string,
  fullRoute: string | undefined
) => {
  const activeProp = {
    active: true,
    'aria-current': 'page'
  };

  if (isActive) {
    return isActive() ? activeProp : {};
  }

  return pathname === fullRoute ? activeProp : {};
};

export interface TabLinkProps extends LinkProps {
  fullRoute?: string | undefined;
  isActive?: (() => boolean) | undefined;
}

const TabLink: React.FC<TabLinkProps> = ({ fullRoute, ...restProps }) => (
  <ScrollableList.Tab as={SportsLink} role="link" {...restProps} />
);

const TabLinkMenu: React.FC<{
  children: (React.ReactElement<TabLinkProps> | null)[];
}> & { Link: typeof TabLink } = ({ children }) => {
  const location = useLocation();
  const aliases = useSelector(select.cms.getAliases);
  const { formatMessage } = useRegulatoryIntl();

  if (!children.length) return null;

  return (
    <ScrollableList
      slotProps={{
        previousButton: {
          'aria-label': formatMessage(ariaLabelScrollLeft)
        },
        nextButton: {
          'aria-label': formatMessage(ariaLabelScrollRight)
        }
      }}
    >
      <>
        {React.Children.map(children, (child) => {
          if (!child || !React.isValidElement(child)) return null;
          const { alias, isActive } = child.props;
          const aliasEntry = aliases[alias as keyof typeof aliases];

          const fullRoute =
            aliasEntry &&
            generateSportsPath(
              aliasEntry,
              child.props.values as Record<string, string | number | boolean | undefined>
            );

          return React.cloneElement(child, {
            fullRoute,
            ...getActiveProp(isActive, location.pathname, fullRoute),
            ...child.props
          });
        })}
      </>
    </ScrollableList>
  );
};

TabLinkMenu.Link = TabLink;
export { TabLinkMenu };
